import { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Link,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import PaginationComponent from '../../components/Pagination/Pagination';
import LoadingButton from '@mui/lab/LoadingButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useGetCourseCompletionReports } from '../../hooks/queries/moodle/useGetCourseCompletionReports';
import { useGetMyBlockingDecision } from '../../hooks/queries/moodle/useGetMyBlockingDecision';
import { useRefreshMyStatus } from '../../hooks/queries/moodle/useRefreshMyStatus';
import RefreshEmployeeModal from './RefreshEmployeeModal';

const COMPLETION_STATE_COLORS = {
  NOT_STARTED: 'default',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
};

const COMPLETION_STATE_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Not Started', value: 'NOT_STARTED' },
  { label: 'In Progress', value: 'IN_PROGRESS' },
  { label: 'Completed', value: 'COMPLETED' },
  { label: 'Not Enrolled', value: 'NOT_ENROLLED' },
  { label: 'Excluded', value: 'EXCLUDED' },
  { label: 'Overdue', value: 'OVERDUE' },
];

const formatDateTime = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString();
};

const CourseReportsComponent = ({ employeeId, showRefreshButton = false }) => {
  const isUserView = !!employeeId || showRefreshButton;
  const [modalOpen, setModalOpen] = useState(false);
  const [stateFilter, setStateFilter] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { reports, totalPages, loading } = useGetCourseCompletionReports({
    employeeId,
    state: stateFilter || undefined,
    page,
    size: pageSize,
  });

  const { blockingDecision } = useGetMyBlockingDecision({
    enabled: isUserView,
  });

  const { mutate: refreshStatus, isPending: isRefreshing } =
    useRefreshMyStatus();

  const colSpan = isUserView ? 7 : 9;

  const downloadAsExcel = () => {
    const rows = reports.map((row) => {
      const base = {
        'Course Name': row.courseName,
        Year: row.year,
        'Completion State': row.completionState,
        'Completion %': row.completionPercent,
        Deadline: formatDateTime(row.deadlineAt),
        'Completed At': formatDateTime(row.completedAt),
        'Last Synced': formatDateTime(row.lastSyncedAt),
      };
      if (!isUserView) {
        return {
          'Employee Name': row.employeeName,
          'Employee Code': row.employeeCode,
          ...base,
        };
      }
      return base;
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Course Compliance');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([wbout], { type: 'application/octet-stream' }),
      `course_compliance_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };

  return (
    <Box padding={2}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        gap={1}
        flexWrap="wrap"
      >
        {!isUserView && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Completion State</InputLabel>
            <Select
              label="Completion State"
              value={stateFilter}
              onChange={(e) => {
                setStateFilter(e.target.value);
                setPage(0);
              }}
            >
              {COMPLETION_STATE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Box display="flex" gap={1} ml="auto">
          {showRefreshButton && (
            <LoadingButton
              variant="outlined"
              startIcon={<RefreshIcon />}
              loading={isRefreshing}
              onClick={() => refreshStatus()}
            >
              Refresh My Status
            </LoadingButton>
          )}
          {!isUserView && (
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => setModalOpen(true)}
            >
              Refresh Employee Status
            </Button>
          )}
          {stateFilter === '' && reports.length > 0 && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={downloadAsExcel}
            >
              Download Excel
            </Button>
          )}
        </Box>
      </Box>
      <RefreshEmployeeModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />

      {isUserView && blockingDecision?.blocked && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>You have been blocked</AlertTitle>
          <Typography variant="body2" mb={1}>
            The following mandatory courses are preventing your access. Complete
            them to restore access.
          </Typography>
          <List dense disablePadding>
            {blockingDecision.blockingCourses?.map((course) => (
              <ListItem
                key={course.courseConfigId}
                disablePadding
                sx={{ pl: 1 }}
              >
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={course.state}
                        color={
                          COMPLETION_STATE_COLORS[course.state] ?? 'default'
                        }
                        size="small"
                      />
                      {course.moodleCourseUrl ? (
                        <Link
                          href={course.moodleCourseUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                        >
                          {course.courseName}
                        </Link>
                      ) : (
                        <span>{course.courseName}</span>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: isUserView ? 900 : 1200 }}>
          <TableHead>
            <TableRow>
              {!isUserView && (
                <>
                  <TableCell
                    sx={{ fontWeight: 600, whiteSpace: 'nowrap', px: 2 }}
                  >
                    Employee Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, px: 2 }}>
                    Employee Code
                  </TableCell>
                </>
              )}
              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', px: 2 }}>
                Course Name
              </TableCell>
              <TableCell sx={{ fontWeight: 600, px: 2 }}>Year</TableCell>
              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', px: 2 }}>
                Completion State
              </TableCell>
              {/* <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', px: 2 }}>
                Completion %
              </TableCell> */}
              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', px: 2 }}>
                Deadline
              </TableCell>
              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', px: 2 }}>
                Completed At
              </TableCell>
              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', px: 2 }}>
                Last Synced
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : reports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center">
                  No course completion data found.
                </TableCell>
              </TableRow>
            ) : (
              reports.map((row, index) => (
                <TableRow
                  key={row.employeeId + row.moodleCourseId + index}
                  hover
                >
                  {!isUserView && (
                    <>
                      <TableCell sx={{ whiteSpace: 'nowrap', px: 2 }}>
                        {row.employeeName}
                      </TableCell>
                      <TableCell sx={{ px: 2 }}>{row.employeeCode}</TableCell>
                    </>
                  )}
                  <TableCell sx={{ whiteSpace: 'nowrap', px: 2 }}>
                    {row.courseName}
                  </TableCell>
                  <TableCell sx={{ px: 2 }}>{row.year}</TableCell>
                  <TableCell sx={{ px: 2 }}>
                    <Chip
                      label={row.completionState}
                      color={
                        COMPLETION_STATE_COLORS[row.completionState] ??
                        'default'
                      }
                      size="small"
                    />
                  </TableCell>
                  {/* <TableCell sx={{ px: 2 }}>{row.completionPercent}%</TableCell> */}
                  <TableCell sx={{ whiteSpace: 'nowrap', px: 2 }}>
                    {formatDateTime(row.deadlineAt)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', px: 2 }}>
                    {formatDateTime(row.completedAt)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', px: 2 }}>
                    {formatDateTime(row.lastSyncedAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2}>
        <PaginationComponent
          count={totalPages || 0}
          page={page + 1}
          pageSize={pageSize}
          pageChange={(event, value) => setPage(value - 1)}
          handlePageSizeChange={(event) => {
            setPageSize(event.target.value);
            setPage(0);
          }}
          paginationTitle="Rows per page"
        />
      </Box>
    </Box>
  );
};

export default CourseReportsComponent;
